import { fetchWithCustomerRequestAuth } from "./customerRequestAuth";

const sentSignatures = new Set();
const inFlightSignatures = new Set();
const retryAfterOnlineSignatures = new Set();
const pendingReports = new Map();
const retryTimers = new Map();

let onlineListenerInstalled = false;
let storedReportsHydrated = false;
const REPORT_RETRY_DELAY_MS = 30_000;
const REPORT_REQUEST_TIMEOUT_MS = 15_000;
const MAX_PERSISTED_REPORTS = 100;
const PENDING_REPORT_STORAGE_KEY = "ael-upload-incident-reports-v1";

function normalizeApiBaseUrl(value) {
  return String(value || "").replace(/\/+$/, "");
}

function isOnline() {
  return (
    typeof navigator === "undefined" ||
    navigator.onLine !== false
  );
}

function readStoredReports() {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(PENDING_REPORT_STORAGE_KEY) || "[]"
    );
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item) =>
          item &&
          typeof item.signature === "string" &&
          item.signature.length <= 500 &&
          item.payload &&
          typeof item.payload === "object" &&
          !Array.isArray(item.payload)
      )
      .slice(-MAX_PERSISTED_REPORTS);
  } catch {
    return [];
  }
}

function persistPendingReports() {
  if (typeof window === "undefined") return;
  try {
    const serialized = [...pendingReports.entries()]
      .slice(-MAX_PERSISTED_REPORTS)
      .map(([signature, report]) => ({
        signature,
        payload: report.payload,
      }));
    if (!serialized.length) {
      window.localStorage.removeItem(PENDING_REPORT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      PENDING_REPORT_STORAGE_KEY,
      JSON.stringify(serialized)
    );
  } catch {
    // Private/sandbox modes may reject localStorage. The in-memory queue and
    // durable upload record still cover the current tab.
  }
}

function hydrateStoredReports(apiBaseUrl) {
  if (storedReportsHydrated || !apiBaseUrl) return;
  storedReportsHydrated = true;
  readStoredReports().forEach(({ signature, payload }) => {
    if (sentSignatures.has(signature) || pendingReports.has(signature)) return;
    pendingReports.set(signature, {
      url: `${apiBaseUrl}/logging/camera-error`,
      payload,
    });
  });
  ensureOnlineListener();
  flushPendingReports();
}

function sendPendingReport(signature) {
  const report = pendingReports.get(signature);
  if (
    !report ||
    sentSignatures.has(signature) ||
    inFlightSignatures.has(signature) ||
    !isOnline()
  ) {
    return;
  }

  inFlightSignatures.add(signature);
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    REPORT_REQUEST_TIMEOUT_MS
  );
  void fetchWithCustomerRequestAuth(report.url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(report.payload),
    keepalive: true,
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) {
        const error = new Error(
          `Upload-Tracking fehlgeschlagen (${response.status}).`
        );
        error.retryable =
          response.status === 401 ||
          response.status === 408 ||
          response.status === 425 ||
          response.status === 429 ||
          response.status >= 500;
        throw error;
      }
      sentSignatures.add(signature);
      pendingReports.delete(signature);
      persistPendingReports();
      const retryTimer = retryTimers.get(signature);
      if (retryTimer) clearTimeout(retryTimer);
      retryTimers.delete(signature);
    })
    .catch((error) => {
      if (error?.retryable === false) {
        pendingReports.delete(signature);
        persistPendingReports();
        return;
      }
      if (!retryTimers.has(signature)) {
        retryTimers.set(
          signature,
          setTimeout(() => {
            retryTimers.delete(signature);
            sendPendingReport(signature);
          }, REPORT_RETRY_DELAY_MS)
        );
      }
    })
    .finally(() => {
      clearTimeout(timeoutId);
      inFlightSignatures.delete(signature);
      if (retryAfterOnlineSignatures.delete(signature)) {
        sendPendingReport(signature);
      }
    });
}

function flushPendingReports() {
  pendingReports.forEach((_report, signature) => {
    if (inFlightSignatures.has(signature)) {
      retryAfterOnlineSignatures.add(signature);
      return;
    }
    sendPendingReport(signature);
  });
}

function ensureOnlineListener() {
  if (onlineListenerInstalled || typeof window === "undefined") return;
  window.addEventListener("online", flushPendingReports);
  onlineListenerInstalled = true;
}

function queueReport(signature, report) {
  if (
    sentSignatures.has(signature) ||
    inFlightSignatures.has(signature) ||
    pendingReports.has(signature)
  ) {
    return;
  }

  pendingReports.set(signature, report);
  persistPendingReports();
  ensureOnlineListener();
  sendPendingReport(signature);
}

function createTrackingEventId(prefix) {
  const randomId =
    globalThis.crypto?.randomUUID?.().replace(/-/g, "") ||
    `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  return `${prefix}:${Date.now()}:${randomId}`;
}

export function reportCameraError({ apiBaseUrl, payload }) {
  const normalizedApiBaseUrl = normalizeApiBaseUrl(apiBaseUrl);
  const vehicleId = String(payload?.vehicleId || "");
  if (!normalizedApiBaseUrl || !vehicleId || !payload) return null;

  hydrateStoredReports(normalizedApiBaseUrl);
  const eventId = createTrackingEventId(`camera:${vehicleId}`);
  const extra =
    payload.extra &&
    typeof payload.extra === "object" &&
    !Array.isArray(payload.extra)
      ? payload.extra
      : {};

  queueReport(eventId, {
    url: `${normalizedApiBaseUrl}/logging/camera-error`,
    payload: {
      ...payload,
      extra: {
        ...extra,
        eventId,
        eventKind: "camera_error",
      },
    },
  });
  return eventId;
}

function getEventKinds(entry) {
  const eventKinds = [];
  if (entry?.persisted === false) {
    eventKinds.push("persistence_unavailable");
  }
  if (
    entry?.state === "pending" &&
    Number(entry?.attempts || 0) > 0
  ) {
    eventKinds.push("first_retry");
  }
  if (entry?.state === "failed") {
    eventKinds.push("terminal_failure");
  }
  return eventKinds;
}

function getEventDetails(entry, eventKind) {
  if (eventKind === "persistence_unavailable") {
    return {
      errorName: "UploadPersistenceError",
      errorCode: entry?.errorCode || "UPLOAD_PERSISTENCE_UNAVAILABLE",
      errorMessage:
        entry?.errorMessage ||
        "Die Upload-Datei konnte nicht dauerhaft im Browser gespeichert werden.",
    };
  }
  if (eventKind === "first_retry") {
    return {
      errorName: entry?.errorName || "UploadRetryScheduled",
      errorCode: entry?.errorCode || "UPLOAD_RETRY_SCHEDULED",
      errorMessage:
        entry?.errorMessage || "Der Upload wird automatisch erneut versucht.",
    };
  }
  return {
    errorName: entry?.errorName || "UploadError",
    errorCode: entry?.errorCode || "UPLOAD_FAILED",
    errorMessage:
      entry?.errorMessage || "Der Upload ist endgültig fehlgeschlagen.",
  };
}

export function reportUploadIncidents({
  apiBaseUrl,
  uploads,
  registryStatus,
  flow,
}) {
  const normalizedApiBaseUrl = normalizeApiBaseUrl(apiBaseUrl);
  if (!normalizedApiBaseUrl || !flow) return;
  hydrateStoredReports(normalizedApiBaseUrl);

  Object.values(uploads || {}).forEach((entry) => {
    const uploadId = String(entry?.uploadId || entry?.key || "");
    if (!uploadId) return;

    const vehicleId = String(entry?.vehicleId || "");
    const eventId = `upload:${flow}:${vehicleId || "unknown"}:${uploadId}`;
    const persistenceAvailable =
      entry?.persisted !== false &&
      registryStatus?.persistenceAvailable !== false;

    getEventKinds(entry).forEach((eventKind) => {
      const signature = `${eventId}:${eventKind}`;
      const details = getEventDetails(entry, eventKind);
      const httpStatus = Number(entry?.httpStatus);
      const extra = {
        flow,
        uploadCategory: entry?.category || null,
        eventId,
        eventKind,
        uploadId,
        errorCode: details.errorCode,
        phase:
          entry?.phase ||
          (eventKind === "terminal_failure" ? "failed" : null),
        attempts: Number(entry?.attempts || 0),
        retrying: eventKind === "first_retry",
        persistenceAvailable,
        ...(Number.isInteger(httpStatus) && httpStatus > 0
          ? { httpStatus }
          : {}),
      };

      queueReport(signature, {
        url: `${normalizedApiBaseUrl}/logging/camera-error`,
        payload: {
          category: `upload_${flow}`,
          errorName: details.errorName,
          errorMessage: details.errorMessage,
          vehicleId: vehicleId || null,
          currentStep: entry?.step || null,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
          pageUrl:
            typeof window !== "undefined"
              ? `${window.location.origin}${window.location.pathname}`
              : null,
          extra,
        },
      });
    });
  });
}
